import { useState, useEffect, useCallback } from 'react';
import { useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { AddressBook, MagnifyingGlass, CaretLeft, CaretRight, Export, ListChecks } from '@phosphor-icons/react';
import { getApiUrl, authFetch } from '../config';
import { formatDate } from '../utils/dateUtils';
import SubPageHeader from '../components/SubPageHeader';
import OptinDetailDialog from './OptinDetailDialog';
import OptinStatus from './OptinStatus';

// Unified row shape — sowohl Cache- als auch Bestand-Rows werden in dieses Format normalisiert
interface UnifiedRow {
  source: 'cache' | 'bestand';
  id: number;
  vnm?: string | null;
  nnm?: string | null;
  plz?: string | null;
  ort?: string | null;
  str?: string | null;
  telefon?: string | null;
  optin?: string | null;
  datum?: string | null;
  // Cache-only
  kampagne_typ?: string | null;
  fc_status?: string | null;
  wkz?: string | null;
  recording_url?: string | null;
  fc_date?: string | null;
  fc_user?: string | null;
  contact_id?: string | null;
  telefon_monat?: string | null;
  // Bestand-only
  inf3?: string | null;
  hkn?: string | null;
}

interface Statistics {
  total: number;
  byOptin: { optin: string; count: number }[];
  byKampagne: { kampagne_typ: string; count: number }[];
  byFcStatus: { fc_status: string; count: number }[];
}

const KAMPAGNE_COLORS: Record<string, string> = {
  CB: '#2563eb',
  IB: '#059669',
  KR: '#d97706',
  UG: '#7c3aed',
};

export default function OptinCache() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeView, setActiveView] = useState<'cache' | 'status'>('cache');

  const [records, setRecords] = useState<UnifiedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterOptin, setFilterOptin] = useState('');
  const [filterKampagne, setFilterKampagne] = useState('');
  const [filterFcStatus, setFilterFcStatus] = useState('');
  const [includeBestand, setIncludeBestand] = useState(false);

  const [stats, setStats] = useState<Statistics | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Detail-Dialog
  const [detailDialog, setDetailDialog] = useState<{ source: 'cache' | 'bestand'; id: number } | null>(null);

  const totalPages = Math.ceil(total / limit);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (appliedSearch) params.set('search', appliedSearch);
      if (filterOptin) params.set('optin', filterOptin);

      let url: string;
      if (includeBestand) {
        // Merge-Modus: Cache + Bestand vereint, Kampagne/FC Status werden ignoriert
        url = getApiUrl(`/optin-cache/merged?${params.toString()}`);
      } else {
        if (filterKampagne) params.set('kampagne_typ', filterKampagne);
        if (filterFcStatus) params.set('fc_status', filterFcStatus);
        url = getApiUrl(`/optin-cache?${params.toString()}`);
      }

      const res = await authFetch(url);
      const data = await res.json();
      const rawRecords = data.records || [];

      // Cache-only Antwort (ohne `source`-Feld) auf Unified-Shape normalisieren
      const normalized: UnifiedRow[] = rawRecords.map((r: any) => ({
        source: r.source || 'cache',
        id: r.id,
        vnm: r.vnm,
        nnm: r.nnm,
        plz: r.plz,
        ort: r.ort,
        str: r.str,
        telefon: r.telefon,
        optin: r.optin,
        datum: r.datum || r.fc_date,
        kampagne_typ: r.kampagne_typ,
        fc_status: r.fc_status,
        wkz: r.wkz,
        recording_url: r.recording_url,
        fc_date: r.fc_date,
        fc_user: r.fc_user,
        contact_id: r.contact_id,
        telefon_monat: r.telefon_monat,
        inf3: r.inf3,
        hkn: r.hkn,
      }));

      setRecords(normalized);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, limit, appliedSearch, filterOptin, filterKampagne, filterFcStatus, includeBestand]);

  const loadStatistics = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await authFetch(getApiUrl('/optin-cache/statistics'));
      const data = await res.json();
      setStats(data);
    } catch { /* ignore */ }
    setLoadingStats(false);
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { loadStatistics(); }, [loadStatistics]);

  const handleSearch = () => {
    setAppliedSearch(search);
    setPage(1);
  };

  const filtersDisabled = includeBestand;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <SubPageHeader title="OptIn Cache" />

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem' }}>
        <h1 style={{ color: '#003366', margin: '0 0 1rem 0', fontSize: isMobile ? '1.5rem' : '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <AddressBook size={28} weight="fill" /> OptIn
        </h1>

        {/* Tab-Navigation */}
        <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb' }}>
          <button onClick={() => setActiveView('cache')}
            style={{
              padding: '10px 24px', cursor: 'pointer', fontWeight: activeView === 'cache' ? 600 : 400,
              color: activeView === 'cache' ? '#003366' : '#6b7280', fontSize: '0.9rem',
              background: activeView === 'cache' ? 'white' : 'transparent',
              border: 'none', borderBottom: `3px solid ${activeView === 'cache' ? '#003366' : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
            <AddressBook size={16} /> Cache & Bestand
          </button>
          <button onClick={() => setActiveView('status')}
            style={{
              padding: '10px 24px', cursor: 'pointer', fontWeight: activeView === 'status' ? 600 : 400,
              color: activeView === 'status' ? '#003366' : '#6b7280', fontSize: '0.9rem',
              background: activeView === 'status' ? 'white' : 'transparent',
              border: 'none', borderBottom: `3px solid ${activeView === 'status' ? '#003366' : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
            <ListChecks size={16} /> Optin Status
          </button>
        </div>

        {activeView === 'status' ? <OptinStatus /> : <>

        {/* Statistik-Karten */}
        {stats && !loadingStats && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Gesamt</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#003366' }}>
                {stats.total.toLocaleString()}
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>OptIn Status</div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
                {stats.byOptin.map(o => (
                  <div key={o.optin || 'null'}>
                    <span style={{
                      fontWeight: 700, fontSize: '1.1rem',
                      color: o.optin === 'Ja' ? '#059669' : o.optin === 'Nein' ? '#dc2626' : '#6b7280',
                    }}>
                      {Number(o.count).toLocaleString()}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.25rem' }}>{o.optin || '-'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Kampagnen</div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                {stats.byKampagne.map(k => (
                  <div key={k.kampagne_typ || 'null'}>
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: KAMPAGNE_COLORS[k.kampagne_typ] || '#6b7280' }}>
                      {Number(k.count).toLocaleString()}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.25rem' }}>{k.kampagne_typ || '-'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>FC Status</div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                {stats.byFcStatus.slice(0, 4).map(s => (
                  <div key={s.fc_status || 'null'}>
                    <span style={{
                      fontWeight: 700, fontSize: '1rem',
                      color: s.fc_status === 'success' ? '#059669' : s.fc_status === 'declined' ? '#dc2626' : '#6b7280',
                    }}>
                      {Number(s.count).toLocaleString()}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.25rem' }}>{s.fc_status || '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filter-Leiste */}
        <div style={{
          backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                Suche {includeBestand ? '(Tokens AND-verknüpft, durchsucht Cache + Bestand)' : '(Name/PLZ/Ort/Contact-ID)'}
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  placeholder={includeBestand ? 'z.B. "Müller 10115"' : 'Name, PLZ, Ort, Contact-ID...'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                  style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  style={{
                    padding: '0.5rem 0.75rem', backgroundColor: '#003366', color: 'white',
                    border: 'none', borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '36px',
                  }}
                >
                  {loading
                    ? <CircularProgress size={14} sx={{ color: 'white' }} />
                    : <MagnifyingGlass size={16} weight="bold" />}
                </button>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>OptIn</label>
              <select
                value={filterOptin}
                onChange={e => { setFilterOptin(e.target.value); setPage(1); }}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
              >
                <option value="">Alle</option>
                <option value="Ja">Ja</option>
                <option value="Nein">Nein</option>
              </select>
            </div>

            <div title={filtersDisabled ? 'Bei Bestand-Suche nicht verfügbar (nur Cache-Spalte)' : ''}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: filtersDisabled ? '#9ca3af' : '#6b7280', marginBottom: '0.25rem' }}>Kampagne</label>
              <select
                value={filterKampagne}
                disabled={filtersDisabled}
                onChange={e => { setFilterKampagne(e.target.value); setPage(1); }}
                style={{
                  padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem',
                  backgroundColor: filtersDisabled ? '#f3f4f6' : 'white',
                  color: filtersDisabled ? '#9ca3af' : 'inherit',
                  cursor: filtersDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <option value="">Alle</option>
                <option value="CB">CB - Callbacks</option>
                <option value="IB">IB - Inbound</option>
                <option value="KR">KR - KüRü</option>
                <option value="UG">UG - Upgrader</option>
              </select>
            </div>

            <div title={filtersDisabled ? 'Bei Bestand-Suche nicht verfügbar (nur Cache-Spalte)' : ''}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: filtersDisabled ? '#9ca3af' : '#6b7280', marginBottom: '0.25rem' }}>FC Status</label>
              <select
                value={filterFcStatus}
                disabled={filtersDisabled}
                onChange={e => { setFilterFcStatus(e.target.value); setPage(1); }}
                style={{
                  padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem',
                  backgroundColor: filtersDisabled ? '#f3f4f6' : 'white',
                  color: filtersDisabled ? '#9ca3af' : 'inherit',
                  cursor: filtersDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <option value="">Alle</option>
                <option value="success">success</option>
                <option value="declined">declined</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={includeBestand}
                  onChange={e => { setIncludeBestand(e.target.checked); setPage(1); }}
                />
                inkl. Bestand
              </label>
            </div>
          </div>
        </div>

        {/* Datentabelle */}
        <div style={{
          backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ color: '#6b7280', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {loading && <CircularProgress size={14} sx={{ color: '#003366' }} />}
              {loading ? 'Lade…' : `${total.toLocaleString()} Ergebnisse${includeBestand ? ' (Cache + Bestand)' : ''}`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
              >
                <CaretLeft size={16} />
              </button>
              <span style={{ fontSize: '0.85rem', color: '#374151' }}>Seite {page} / {totalPages || 1}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
              >
                <CaretRight size={16} />
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto', position: 'relative', opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s ease' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  {includeBestand && <th style={{ padding: '0.5rem', textAlign: 'left' }}>Quelle</th>}
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>OptIn</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>PLZ/Ort</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Stra&szlig;e</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Telefon</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Datum</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Info</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Rec.</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr
                    key={`${r.source}-${r.id}`}
                    onClick={() => setDetailDialog({ source: r.source, id: r.id })}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    {includeBestand && (
                      <td style={{ padding: '0.4rem 0.5rem' }}>
                        <span style={{
                          padding: '1px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 600,
                          backgroundColor: r.source === 'cache' ? '#dbeafe' : '#ede9fe',
                          color: r.source === 'cache' ? '#1e40af' : '#5b21b6',
                        }}>
                          {r.source === 'cache' ? 'Cache' : 'Bestand'}
                        </span>
                      </td>
                    )}
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '1px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: r.optin === 'Ja' ? '#dcfce7' : r.optin === 'Nein' ? '#fecaca' : '#f3f4f6',
                        color: r.optin === 'Ja' ? '#166534' : r.optin === 'Nein' ? '#991b1b' : '#374151',
                      }}>
                        {r.optin || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{r.vnm || ''} {r.nnm || ''}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.plz || ''} {r.ort || ''}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.str || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.telefon || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {formatDate(r.datum || '')}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', fontSize: '0.75rem' }}>
                      {r.source === 'cache' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {r.kampagne_typ && (
                            <span style={{ fontWeight: 600, color: KAMPAGNE_COLORS[r.kampagne_typ] || '#6b7280' }}>
                              {r.kampagne_typ}
                            </span>
                          )}
                          {r.fc_status && (
                            <span style={{
                              padding: '0 6px', borderRadius: '4px', fontSize: '0.7rem',
                              backgroundColor: r.fc_status === 'success' ? '#dcfce7' : r.fc_status === 'declined' ? '#fee2e2' : '#f3f4f6',
                              color: r.fc_status === 'success' ? '#166534' : r.fc_status === 'declined' ? '#991b1b' : '#374151',
                            }}>
                              {r.fc_status}
                            </span>
                          )}
                          {r.wkz && (
                            <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#6b7280' }}>{r.wkz}</span>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {r.inf3 && (
                            <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#6b7280' }}>{r.inf3}</span>
                          )}
                          {r.hkn && (
                            <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{r.hkn}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      {r.recording_url ? (
                        <a
                          href={r.recording_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ color: '#2563eb' }}
                          title="Aufzeichnung"
                        >
                          <Export size={14} />
                        </a>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
                {records.length === 0 && !loading && (
                  <tr><td colSpan={includeBestand ? 9 : 8} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>Keine Daten gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
              >
                <CaretLeft size={16} />
              </button>
              <span style={{ fontSize: '0.85rem', color: '#374151' }}>Seite {page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
              >
                <CaretRight size={16} />
              </button>
            </div>
          )}
        </div>

        {detailDialog && (
          <OptinDetailDialog
            source={detailDialog.source}
            id={detailDialog.id}
            onClose={() => setDetailDialog(null)}
          />
        )}
        </>}
      </main>
    </div>
  );
}
